import type { CommonPowerSyncDatabase } from '@powersync/common';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { createDemoDatabase } from './adapter';
import { insertDemoRow, toSqlBool, type DemoDb } from './sql';
import { DEMO_TABLES, type DemoRowValues } from './tables';
import {
  DEMO_CATALOG_VERSION,
  DEMO_CATALOG_VERSION_STORAGE_KEY,
  DEMO_PRODUCTS,
  type DemoProductData,
} from './demoData';

let demoDatabasePromise: Promise<CommonPowerSyncDatabase> | null = null;

/** Opens the demo database once; after a failed open, the next call retries. */
export function getDemoDatabase(): Promise<CommonPowerSyncDatabase> {
  if (!demoDatabasePromise) {
    demoDatabasePromise = createDemoDatabase().catch((error: unknown) => {
      demoDatabasePromise = null;
      console.error('[DemoDatabase] Setup error:', error);
      throw error;
    });
  }
  return demoDatabasePromise;
}

// Flag to track if seeding has been done this session
let isSeeded = false;

async function getStoredCatalogVersion(): Promise<number | null> {
  try {
    const raw = await AsyncStorage.getItem(DEMO_CATALOG_VERSION_STORAGE_KEY);
    if (raw == null) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

async function setStoredCatalogVersion(version: number): Promise<void> {
  await AsyncStorage.setItem(DEMO_CATALOG_VERSION_STORAGE_KEY, String(version));
}

function toProductRowValues(
  productData: DemoProductData,
): DemoRowValues<'demo_products'> {
  return {
    bp_owner_id: productData.bp_owner_id,
    bp_product_location_id: productData.bp_product_location_id,
    bp_product_barcode: productData.bp_product_barcode,
    bp_product_name: productData.bp_product_name,
    bp_product_category: productData.bp_product_category,
    bp_product_sub_category: productData.bp_product_sub_category,
    bp_product_price_type: productData.bp_product_price_type,
    bp_product_kind: productData.bp_product_kind ?? 'product',
    bp_product_unit_of_mass: productData.bp_product_unit_of_mass,
    bp_product_mass: productData.bp_product_mass,
    // Variant parents may seed null → 0, as the WatermelonDB columns did
    bp_product_buying_price: productData.bp_product_buying_price ?? 0,
    bp_product_selling_price: productData.bp_product_selling_price ?? 0,
    bp_product_quantity: productData.bp_product_quantity ?? 0,
    bp_product_size: productData.bp_product_size,
    bp_minimum_stock_level: productData.bp_minimum_stock_level,
    bp_product_model: productData.bp_product_model,
    bp_product_image: productData.bp_product_image,
    bp_product_deleted: toSqlBool(productData.bp_product_deleted),
    bp_product_is_favourite: toSqlBool(productData.bp_product_is_favourite),
    bp_product_created_at: productData.bp_product_created_at,
    bp_product_updated_at: productData.bp_product_updated_at,
    bp_product_has_variants: toSqlBool(productData.bp_product_has_variants),
    bp_product_variant_attributes: JSON.stringify(
      productData.bp_product_variant_attributes ?? [],
    ),
    bp_product_variants: JSON.stringify(productData.bp_product_variants ?? []),
  };
}

async function writeDemoProducts(db: DemoDb): Promise<void> {
  for (const productData of DEMO_PRODUCTS) {
    await insertDemoRow(db, 'demo_products', toProductRowValues(productData));
  }
}

// Seed demo products if needed. Catalog version mismatch forces full clear + reseed.
export async function seedDemoProducts(): Promise<void> {
  try {
    const storedVersion = await getStoredCatalogVersion();
    // Allow mid-session catalog bumps to force reseed even if already seeded
    if (storedVersion !== DEMO_CATALOG_VERSION) {
      isSeeded = false;
    }

    if (isSeeded) {
      return;
    }

    const db = await getDemoDatabase();
    const { count: existingCount } = await db.get<{ count: number }>(
      'SELECT COUNT(*) AS count FROM demo_products',
    );
    const needsReseed =
      storedVersion !== DEMO_CATALOG_VERSION || existingCount === 0;

    if (needsReseed) {
      if (existingCount > 0 || storedVersion !== DEMO_CATALOG_VERSION) {
        // Clear stale demo rows so the new catalog always wins
        await clearDemoData({ preserveSeedFlag: true });
      }

      await db.writeTransaction(async tx => {
        await writeDemoProducts(tx);
      });
      await setStoredCatalogVersion(DEMO_CATALOG_VERSION);
      console.log(
        '[DemoDatabase] Seeded',
        DEMO_PRODUCTS.length,
        'demo products (catalog v' + DEMO_CATALOG_VERSION + ')',
      );
    }

    isSeeded = true;
  } catch (error) {
    console.error('[DemoDatabase] Seed error:', error);
    throw error;
  }
}

// Clear all demo data (useful for resetting demo flow)
export async function clearDemoData(options?: {
  preserveSeedFlag?: boolean;
}): Promise<void> {
  try {
    const db = await getDemoDatabase();
    await db.writeTransaction(async tx => {
      for (const table of Object.keys(DEMO_TABLES)) {
        await tx.execute(`DELETE FROM ${table}`);
      }
    });
    if (!options?.preserveSeedFlag) {
      isSeeded = false;
      try {
        await AsyncStorage.removeItem(DEMO_CATALOG_VERSION_STORAGE_KEY);
      } catch {
        // ignore storage clear failures
      }
    }
    console.log('[DemoDatabase] Cleared all demo data');
  } catch (error) {
    console.error('[DemoDatabase] Clear error:', error);
    throw error;
  }
}

export type { DemoRow } from './tables';
export {
  DEMO_PRODUCTS,
  DEMO_CATALOG_VERSION,
  DEMO_CATALOG_VERSION_STORAGE_KEY,
} from './demoData';
